import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pill, Search } from "lucide-react";

import { apiError } from "../../../shared/api/client";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../../shared/api/clinicFeatures";
import { inventoryApi, inventoryQueryKeys } from "../../../shared/api/inventory";
import { medicinesApi } from "../../../shared/api/medicines";
import {
  prescriptionTemplateQueryKeys,
  prescriptionTemplatesApi,
} from "../../../shared/api/prescriptionTemplates";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { useOperatesModule } from "../../../shared/hooks/useOperatesModule";
import type { MedicineSummaryResponse } from "../../../shared/types/medicine";
import type {
  PrescriptionTemplateResponse,
  PrescriptionTemplateSummaryResponse,
} from "../../../shared/types/prescriptionTemplate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

interface Props {
  open: boolean;
  onClose: () => void;
  onPickTemplate: (template: PrescriptionTemplateResponse) => void;
  onPickMedicine: (medicine: MedicineSummaryResponse) => void;
}

/**
 * One search box for the whole prescribing step.
 *
 * The doctor types once; results come back in two groups — prescription
 * templates on top, individual medicines below, separated by a rule. Before
 * this there were two buttons and two modals, which forced a decision ("am I
 * looking for a template or a drug?") before the search that would have
 * answered it.
 *
 * Each medicine shows on-hand stock, aggregated across batches from the
 * dispensing endpoint. At zero or below it is marked "Hết hàng" but stays
 * selectable — a doctor may legitimately prescribe something the clinic does
 * not stock and the patient buys outside; hiding it would silently change
 * clinical behaviour.
 */
export function RxSearchPicker({ open, onClose, onPickTemplate, onPickMedicine }: Props) {
  const [q, setQ] = useState("");
  const debounced = useDebounce(q, 200);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const templates = useQuery({
    queryKey: prescriptionTemplateQueryKeys.list({ q: debounced || null, visibleOnly: true }, 0),
    queryFn: () =>
      prescriptionTemplatesApi.list(
        { q: debounced || null, visibleOnly: true },
        { page: 0, size: 15, sort: "name" },
      ),
    enabled: open,
  });

  const medicines = useQuery({
    queryKey: ["medicines-rx-picker", debounced],
    queryFn: () =>
      medicinesApi.list(
        { q: debounced || null, visibleOnly: true },
        { page: 0, size: 30, sort: "name" },
      ),
    enabled: open,
  });

  // Batch-level rows; summed per medicine for the "còn N" badge. Stock is
  // informational here, so a failure must not block prescribing.
  // Tồn kho là vùng Tồn kho, bán riêng (ADR-0053) — xem MedicinePicker.
  const inventory = useOperatesModule("INVENTORY");
  const stock = useQuery({
    queryKey: inventoryQueryKeys.stock(),
    queryFn: () => inventoryApi.stock(),
    enabled: open && inventory.loaded && inventory.operates,
    staleTime: 30_000,
  });

  // Tồn kho ("Còn N" / "Hết hàng") chỉ hiện khi phòng khám bật "Cho phép bán thuốc" (ADR-0027).
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const showStock = features.data?.allowSelling ?? false;

  const onHandByMedicine = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of stock.data ?? []) {
      map.set(row.medicineId, (map.get(row.medicineId) ?? 0) + (row.quantityOnHand ?? 0));
    }
    return map;
  }, [stock.data]);

  const templateRows = templates.data?.content ?? [];
  const medicineRows = medicines.data?.content ?? [];
  const loading = templates.isLoading || medicines.isLoading;
  const nothing = !loading && templateRows.length === 0 && medicineRows.length === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[820px] gap-4">
        <DialogHeader>
          <DialogTitle>Thêm vào đơn thuốc</DialogTitle>
          <DialogDescription>
            Gõ một lần để tìm cả toa mẫu và thuốc — theo mã, tên, hoạt chất, chẩn đoán hoặc ICD-10.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm toa mẫu hoặc thuốc…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-[60vh] space-y-1 overflow-y-auto">
          {loading && <p className="text-sm text-muted-foreground">Đang tải…</p>}

          {templates.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError(templates.error).detail}
            </div>
          )}
          {medicines.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError(medicines.error).detail}
            </div>
          )}

          {nothing && (
            <div className="flex items-start gap-3 rounded-md border border-dashed border-input bg-muted/40 px-4 py-5">
              <Pill className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="space-y-1 text-sm">
                <strong className="font-medium text-foreground">
                  Không tìm thấy toa mẫu hay thuốc nào khớp.
                </strong>
                <p className="text-muted-foreground">
                  Tạo toa mẫu tại{" "}
                  <a className="text-primary underline-offset-4 hover:underline" href="/prescriptions/templates">
                    Toa thuốc mẫu
                  </a>
                  , tạo thuốc tại{" "}
                  <a className="text-primary underline-offset-4 hover:underline" href="/settings/medicines">
                    Cài đặt → Thuốc
                  </a>
                  .
                </p>
              </div>
            </div>
          )}

          {/* ── Phần 1: toa thuốc mẫu ── */}
          {templateRows.length > 0 && (
            <>
              <div className="px-1 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Toa thuốc mẫu
              </div>
              {templateRows.map((t) => (
                <TemplateRow key={t.id} t={t} onPick={onPickTemplate} onClose={onClose} />
              ))}
            </>
          )}

          {/* ── Ngăn cách 2 nhóm ── */}
          {templateRows.length > 0 && medicineRows.length > 0 && (
            <Separator className="my-2" role="separator" aria-hidden="true" />
          )}

          {/* ── Phần 2: thuốc ── */}
          {medicineRows.length > 0 && (
            <>
              <div className="px-1 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Thuốc
              </div>
              {medicineRows.map((m) => (
                <MedicineRow
                  key={m.id}
                  m={m}
                  onHand={onHandByMedicine.get(m.id)}
                  stockKnown={showStock && !stock.isLoading && !stock.isError}
                  onPick={(picked) => {
                    onPickMedicine(picked);
                    onClose();
                  }}
                />
              ))}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateRow({
  t,
  onPick,
  onClose,
}: {
  t: PrescriptionTemplateSummaryResponse;
  onPick: (full: PrescriptionTemplateResponse) => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          <span className="mr-2 font-mono text-muted-foreground">{t.code}</span>
          {t.name}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {t.icd10Code && <span className="mr-2 font-mono">{t.icd10Code}</span>}
          {t.diagnosis && <span>{t.diagnosis} · </span>}
          {t.durationDays} ngày · {t.itemCount} thuốc
        </div>
      </div>
      <div className="shrink-0">
        <Button
          type="button"
          size="sm"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              const full = await prescriptionTemplatesApi.get(t.id);
              onPick(full);
              onClose();
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? "Đang nạp…" : "Dùng mẫu này"}
        </Button>
      </div>
    </div>
  );
}

function MedicineRow({
  m,
  onHand,
  stockKnown,
  onPick,
}: {
  m: MedicineSummaryResponse;
  onHand: number | undefined;
  stockKnown: boolean;
  onPick: (m: MedicineSummaryResponse) => void;
}) {
  // A medicine with no batch row has no stock at all — that is 0, not "unknown".
  const qty = stockKnown ? (onHand ?? 0) : null;
  const out = qty !== null && qty <= 0;

  return (
    <div className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="truncate">{m.name}</span>
          {qty !== null && (
            <Badge variant={out ? "destructive" : "success"}>
              {out ? "Hết hàng" : `Còn ${qty.toLocaleString("vi-VN")} ${m.unit}`}
            </Badge>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          <span className="font-mono">{m.internalCode}</span>
          {m.strength && ` · ${m.strength}`}
          {m.activeIngredient && ` · ${m.activeIngredient}`}
        </div>
      </div>
      <div className="shrink-0">
        <Button type="button" size="sm" onClick={() => onPick(m)}>
          Chọn
        </Button>
      </div>
    </div>
  );
}
